import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ShoppingCart, Send, RefreshCw, Loader2, Clock, CheckCircle,
  AlertCircle, Phone, Mail, ExternalLink, TrendingUp, DollarSign, Bell
} from 'lucide-react';

const REMINDER_BADGES = {
  reminded: { label: 'Sudah Diingatkan', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  scheduled: { label: 'Terjadwal', color: 'bg-blue-100 text-blue-700', icon: Clock },
  no_reminder: { label: 'Belum Diingatkan', color: 'bg-red-100 text-red-700', icon: AlertCircle },
};

function formatRp(v) {
  return 'Rp ' + Number(v || 0).toLocaleString('id-ID');
}

function formatDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function timeSince(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins} menit lalu`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} jam lalu`;
  const days = Math.floor(hours / 24);
  return `${days} hari lalu`;
}

export default function AbandonedCarts() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({
    total_abandoned: 0, total_value: 0, recovered: 0,
    revenue_recovered: 0, recovery_rate: 0, reminders_today: 0, total_reminded: 0
  });
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const [sendingId, setSendingId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [listRes, statsRes] = await Promise.all([
        api.get('/tenant/abandoned-carts', { params: { status: filterStatus, page, limit: 20 } }),
        api.get('/tenant/abandoned-carts/stats')
      ]);
      setItems(listRes.data.items || []);
      setTotal(listRes.data.total || 0);
      setStats(statsRes.data);
    } catch {
      showToast('Gagal memuat data', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRemind = async (orderId) => {
    setSendingId(orderId);
    try {
      const res = await api.post(`/tenant/abandoned-carts/${orderId}/remind`);
      if (res.data.wa_link) {
        window.open(res.data.wa_link, '_blank', 'noopener');
        showToast('Link WhatsApp dibuka — kirim pesan secara manual');
      } else if (res.data.status === 'sent') {
        showToast(`Reminder terkirim via ${res.data.sent_via}`);
      } else {
        showToast('Reminder gagal dikirim', 'error');
      }
      fetchData();
    } catch (e) {
      showToast(e.response?.data?.detail || 'Gagal mengirim reminder', 'error');
    } finally {
      setSendingId(null);
    }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart className="h-6 w-6 text-red-500" />
            Abandoned Cart
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pantau pesanan yang belum dibayar dan kirim reminder otomatis
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <ShoppingCart className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Abandoned</p>
                <p className="text-xl font-bold">{stats.total_abandoned}</p>
                <p className="text-xs text-muted-foreground">{formatRp(stats.total_value)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Recovered</p>
                <p className="text-xl font-bold">{stats.recovered}</p>
                <p className="text-xs text-emerald-600 font-medium">{stats.recovery_rate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Revenue Recovered</p>
                <p className="text-xl font-bold">{formatRp(stats.revenue_recovered)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Bell className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Reminder Hari Ini</p>
                <p className="text-xl font-bold">{stats.reminders_today}</p>
                <p className="text-xs text-muted-foreground">{stats.total_reminded} total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Daftar Pesanan Belum Bayar</CardTitle>
            <div className="flex gap-2">
              {[
                { v: '', l: 'Semua' },
                { v: 'no_reminder', l: 'Belum Diingatkan' },
                { v: 'scheduled', l: 'Terjadwal' },
                { v: 'reminded', l: 'Sudah Diingatkan' },
              ].map(f => (
                <Button key={f.v} variant={filterStatus === f.v ? 'default' : 'outline'} size="sm"
                  onClick={() => { setFilterStatus(f.v); setPage(1); }}>
                  {f.l}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">Tidak ada cart yang ditinggalkan</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map(item => {
                const badge = REMINDER_BADGES[item.reminder_status] || REMINDER_BADGES.no_reminder;
                const BadgeIcon = badge.icon;
                return (
                  <div key={item.id} className="border rounded-lg p-4 hover:bg-accent/30 transition">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0 space-y-2">
                        {/* Customer info */}
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-semibold">{item.customer_name || 'Customer'}</span>
                          {item.order_number && (
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                              #{item.order_number}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${badge.color}`}>
                            <BadgeIcon className="h-3 w-3" />
                            {badge.label}
                          </span>
                        </div>

                        {/* Contact */}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          {item.customer_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {item.customer_phone}
                            </span>
                          )}
                          {item.customer_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {item.customer_email}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {timeSince(item.created_at)}
                          </span>
                        </div>

                        {/* Items */}
                        <div className="flex flex-wrap gap-1">
                          {(item.items || []).map((p, i) => (
                            <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                              {p.name} x{p.qty}
                            </span>
                          ))}
                        </div>

                        {/* Reminder progress */}
                        {item.reminders_sent > 0 && (
                          <p className="text-xs text-muted-foreground">
                            Reminder terkirim: {item.reminders_sent} | Pending: {item.reminders_pending}
                          </p>
                        )}
                      </div>

                      {/* Right side: total + action */}
                      <div className="text-right shrink-0 space-y-2">
                        <p className="font-bold text-lg">{formatRp(item.total)}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(item.created_at)}</p>
                        <Button
                          size="sm"
                          onClick={() => handleRemind(item.id)}
                          disabled={sendingId === item.id}
                        >
                          {sendingId === item.id ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4 mr-1" />
                          )}
                          Kirim Reminder
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 pt-4">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                    Prev
                  </Button>
                  <span className="flex items-center text-sm text-muted-foreground px-2">
                    {page} / {totalPages}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white ${
          toast.type === 'error' ? 'bg-red-500' : 'bg-emerald-500'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
